import { markdownToPlainText } from "@/lib/markdown-plain-text";
import { isListNumberToken } from "./tts-speech-segments";

// A URL ends on a character that is not sentence punctuation, so "Read
// https://x.com/a. Then" keeps its full stop; balanced parens stay inside.
const BARE_URL =
  /\s*https?:\/\/(?:[^\s()]|\([^\s()]*\))*(?:[^\s().,;:!?'"]|\([^\s()]*\))/g;

// Markdown is stripped before whitespace collapses, so line-start list
// markers still read as markers. Bare URLs are never read character by character.
export function plainSpeechText(text: string): string {
  return markdownToPlainText(text)
    .replace(BARE_URL, "")
    .replace(/\s+/g, " ")
    .trim();
}

export function normalizedSpeechText(text: string): string {
  return plainSpeechText(text)
    .toLowerCase()
    .replace(/[‘’]/g, "'")
    .replace(/[“”]/g, '"')
    .replace(/[—–]/g, "-")
    .replace(/[^\p{L}\p{N}]+/gu, " ")
    .replace(/\s+/g, " ")
    .trim();
}

type SpeechWord = { word: string; token: number; listNumber: boolean };

// Emphasis markers pair differently in a truncated prefix and in the whole
// final (`user_id` stays in one, joins to `userid` in the other), so they
// are dropped before the words are compared.
function speechWords(tokens: string[]): SpeechWord[] {
  return tokens.flatMap((token, index) =>
    normalizedSpeechText(token.replace(/[*_~`]/g, ""))
      .split(" ")
      .filter(Boolean)
      .map((word) => ({
        word,
        token: index,
        listNumber: isListNumberToken(token),
      })),
  );
}

/**
 * The index of the first `finalPlain` whitespace token the spoken prefix
 * does not cover, or -1 when the final does not start with it. The only
 * place that compares a spoken prefix with a final. The streamed prefix is
 * whitespace-flattened, so a list, heading or quote marker that the final
 * strips at a line start sits mid-line in the prefix: wordless tokens vanish
 * on both sides, and a flattened list number is skipped on the side that
 * still carries it.
 */
function coveredFinalTokens(finalPlain: string, prefixPlain: string): number {
  const finalTokens = finalPlain.split(/\s+/);
  const finalWords = speechWords(finalTokens);
  const prefixWords = speechWords(prefixPlain.split(/\s+/));
  let finalIndex = 0;
  let prefixIndex = 0;
  while (prefixIndex < prefixWords.length) {
    const finalWord = finalWords[finalIndex];
    if (!finalWord) return -1;
    const prefixWord = prefixWords[prefixIndex]!;
    // "1. 1 file modified": the marker must not consume the item's own number.
    const markerBeforeItsItem =
      prefixWord.listNumber &&
      !finalWord.listNumber &&
      prefixWords[prefixIndex + 1]?.word === finalWord.word;
    if (finalWord.word === prefixWord.word && !markerBeforeItsItem) {
      finalIndex += 1;
      prefixIndex += 1;
    } else if (prefixWord.listNumber) {
      prefixIndex += 1;
    } else if (finalWord.listNumber) {
      finalIndex += 1;
    } else {
      return -1;
    }
  }
  const nextWord = finalWords[finalIndex];
  // Nothing speakable is left once every word of the final is covered.
  if (!nextWord) return finalTokens.length;
  // A token the prefix ends inside stays whole in the tail: repeating part of
  // it is better than dropping the rest.
  const coveredToken = finalWords[finalIndex - 1]?.token ?? -1;
  return Math.min(nextWord.token, coveredToken + 1);
}

export function remainingTextAfterPrefix(
  finalText: string,
  prefixText: string,
): string {
  const finalPlain = plainSpeechText(finalText);
  const prefixPlain = plainSpeechText(prefixText);
  if (!finalPlain) return "";
  if (!prefixPlain) return finalPlain;
  const covered = coveredFinalTokens(finalPlain, prefixPlain);
  if (covered < 0) return finalPlain;
  return finalPlain.split(/\s+/).slice(covered).join(" ");
}

export function finalTextStartsWithPrefix(
  finalText: string,
  prefixText: string,
): boolean {
  const prefixPlain = plainSpeechText(prefixText);
  if (!prefixPlain) return false;
  return coveredFinalTokens(plainSpeechText(finalText), prefixPlain) >= 0;
}
